'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Input } from './input';
import { cn } from './lib/utils';

// ---------------------------------------------------------------------------
// SelectField — flat options-array select, ported from the site-builder
// blocks package's `SbSelect` (`_shared/SbSelect.tsx`) onto the Radix
// `Select` primitives above (button trigger, not a text-input trigger —
// that's what `Combobox` is for). Keeps `SbSelect`'s real call-site surface:
// a caption/icon-aware trigger, a `panelWidth="trigger"` option, and an
// auto-activated in-panel filter box once the option list gets long.
// ---------------------------------------------------------------------------

export interface SelectFieldOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps {
  value: string | undefined;
  onChange: (value: string) => void;
  options: SelectFieldOption[];
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  /** Optional leading glyph rendered inside the trigger, before the value. */
  icon?: React.ReactNode;
  /** Optional small label stacked above the value inside the trigger. */
  caption?: string;
  /** Collapse the trigger to the {@link caption} alone, dropping the value line. */
  captionOnly?: boolean;
  /** {@code 'trigger'} pins the panel to the trigger's width. */
  panelWidth?: 'auto' | 'trigger';
  /** Preferred open side/corner, e.g. {@code 'bottom-end'}. Radix's own
   *  collision detection still flips it away from the viewport edge when
   *  there isn't room — this only sets the *preferred* side/alignment. */
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  /** {@code 'auto'} (default) shows the in-panel filter box once the list is
   *  longer than 10 options; {@code true}/{@code false} force it. */
  searchable?: boolean | 'auto';
  searchPlaceholder?: string;
  noResultsText?: string;
  className?: string;
  style?: React.CSSProperties;
}

const SEARCH_THRESHOLD = 10;

export function SelectField({
  value,
  onChange,
  options,
  placeholder,
  ariaLabel,
  disabled = false,
  fullWidth = false,
  icon,
  caption,
  captionOnly = false,
  panelWidth = 'auto',
  placement = 'bottom-start',
  searchable = 'auto',
  searchPlaceholder,
  noResultsText,
  className,
  style,
}: SelectFieldProps) {
  const { t } = useTranslation('ui');
  const resolvedPlaceholder = placeholder ?? t('common:placeholders.select');
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('common:placeholders.search');
  const side = placement.startsWith('top') ? 'top' : 'bottom';
  const align = placement.endsWith('end') ? 'end' : 'start';
  const [query, setQuery] = React.useState('');
  const showSearch =
    searchable === true || (searchable === 'auto' && options.length > SEARCH_THRESHOLD);
  const selected = options.find((o) => o.value === value);
  const visible =
    showSearch && query.trim() ? options.filter((o) => matches(o.label, query)) : options;

  return (
    <Select
      value={value}
      onValueChange={onChange}
      disabled={disabled}
      onOpenChange={(open) => {
        if (!open) setQuery('');
      }}
    >
      <SelectTrigger
        fill={fullWidth}
        aria-label={ariaLabel}
        className={cn(className)}
        style={style}
      >
        {icon ? (
          <span className="inline-flex items-center text-muted-foreground">{icon}</span>
        ) : null}
        {caption && captionOnly ? (
          // Explicit muted color — Radix's own data-placeholder only fires for a literal '' value.
          <span className="flex min-w-0 flex-1 flex-col items-start text-left">
            <span className="truncate text-muted-foreground">{caption}</span>
          </span>
        ) : caption ? (
          <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
            <span className="truncate text-xs text-muted-foreground">{caption}</span>
            <span className="truncate">{selected ? selected.label : resolvedPlaceholder}</span>
          </span>
        ) : (
          <SelectValue placeholder={resolvedPlaceholder} />
        )}
      </SelectTrigger>
      <SelectContent
        side={side}
        align={align}
        style={
          panelWidth === 'trigger' ? { width: 'var(--radix-select-trigger-width)' } : undefined
        }
      >
        {showSearch && (
          <div className="p-1 pb-0" onKeyDown={(e) => e.stopPropagation()}>
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={resolvedSearchPlaceholder}
              aria-label={resolvedSearchPlaceholder}
              className="h-8"
            />
          </div>
        )}
        {visible.length === 0 ? (
          <div className="px-2 py-2 text-center text-sm text-muted-foreground">
            {noResultsText ?? t('common:messages.noResults')}
          </div>
        ) : (
          visible.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

/** Fold case and accents so "sehir" matches "Şehir" and "IZMIR" matches
 *  "İzmir" — ported from `SbSelect`. */
function fold(text: string): string {
  return text.toLocaleLowerCase('tr').normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Substring match on any whitespace-separated term, so "ank yuz" finds
 *  "Ankara — Yüz Yüze". */
function matches(label: string, query: string): boolean {
  const haystack = fold(label);
  return fold(query)
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}
